import React, { useState, useRef } from 'react';

import useFocusOn from './hooks/useFocusOn';
import dialogProps from '../types/dialogProps';
import useDisableBodyScroll from './hooks/useDisableScroll';
import useEscKeyPress from './hooks/useEscKeyPress';
import useInertSiblings from './hooks/useInertSiblings/useInertSiblings';
import useNetlifyFormRequest, { requestStatuses } from '../../Forms/hooks/useNetlifyFormRequest';

import FormField from '../../Forms/FormField/FormField';
import Modal from './Modal/Modal';
import Button, { buttonTypes } from '../../Buttons/Button/Button';
import FormSubmitionError from '../../Forms/FormNotifications/FormSubmitionError';
import FormSubmitionSuccess from '../../Forms/FormNotifications/FormSubmitionSuccess';
import { honeyPotName } from '../../Forms/FormField/Inputs/Honeypot/Honeypot';

const Contact = ({ onClose, status, selector }: dialogProps): JSX.Element => {
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        company: '',
        message: '',
        [honeyPotName]: ''
    });

    const handleFieldChange = (key: string, value: string) => setFormState((state) => {
        return { ...state, [key]: value };
    });

    const inputRef = useRef<HTMLInputElement>(null);

    useInertSiblings(selector as string);
    useEscKeyPress(onClose);
    useFocusOn(inputRef);
    useDisableBodyScroll('body');

    const labelledBy = 'dialog-contact-labelled-by';

    const { netlifySubmit, requestStatus } = useNetlifyFormRequest();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (typeof window !== 'undefined') {
            const data = {
                event_category: 'general_enquiry',
                event_label: 'General enquiry',
            };

            window.gtag('event', 'form_submit', { ...data });
        }
        netlifySubmit('contact', formState);
    };

    return (
        <Modal>
            <Modal.Mask onClick={onClose} status={status} />
            <Modal.Dialog labelledBy={labelledBy} status={status}>
                <Modal.Header onClose={onClose} labelledBy={labelledBy}>Contact us</Modal.Header>
                <form onSubmit={handleSubmit}>
                    <FormField.Honeypot value={formState[honeyPotName]} onChange={(e) => handleFieldChange(honeyPotName, e.target.value)} />
                    <div className="mb-7">
                        <FormField id="name" label="Name">
                            <FormField.TextInput
                                id="name"
                                required
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('name', e.target.value)}
                                value={formState.name}
                            />
                        </FormField>
                    </div>
                    <div className="mb-7">
                        <FormField id="email" label="Email">
                            <FormField.Email
                                id="email"
                                required
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('email', e.target.value)}
                                value={formState.email}
                            />
                        </FormField>
                    </div>
                    <div className="mb-7">
                        <FormField id="company" label="Company" optional>
                            <FormField.TextInput
                                id="company"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('company', e.target.value)}
                                value={formState.company}
                            />
                        </FormField>
                    </div>
                    <div className="mb-7">
                        <FormField id="message" label="Message">
                            <FormField.Textarea
                                id="message"
                                required
                                rows={4}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('message', e.target.value)}
                                value={formState.message}
                            />
                        </FormField>
                    </div>
                    {requestStatus === requestStatuses.fail ? (
                        <div className="mb-7">
                            <FormSubmitionError>
                                Sorry, there was an error sending your request, please try again.
                            </FormSubmitionError>
                        </div>
                    ) : null}
                    {requestStatus === requestStatuses.success ? (
                        <div className="mb-7">
                            <FormSubmitionSuccess>
                                Thank you for contacting us, we will be in touch shortly.
                            </FormSubmitionSuccess>
                        </div>
                    ) : null}
                    {requestStatus === requestStatuses.success ? (
                        <Button type={buttonTypes.button} title="Close" onClick={onClose} size="fullWidth">
                            Close
                        </Button>
                    ) : (
                        <Button type={buttonTypes.submit} title="Send message" size="fullWidth">
                            Send message
                        </Button>
                    )}
                </form>
            </Modal.Dialog>
        </Modal>
    );
};

export default Contact;
